// Stock calculations. The breakdown and the transfer suggestions are the same formulas the page
// has always used, moved out of the screen so they can be checked on their own.

export const PRODUCT_CATEGORIES = [
  { id: 'motorina', name: 'Motorină', unit: 'litri' },
  { id: 'samanta', name: 'Samanta', unit: 'kg' },
  { id: 'ingrasamant', name: 'Îngrășământ', unit: 'kg' },
  { id: 'erbicide', name: 'Erbicide', unit: 'litri' },
  { id: 'fungicide', name: 'Fungicide', unit: 'litri' },
  { id: 'insecticide', name: 'Insecticide', unit: 'litri' },
  { id: 'adjuvant', name: 'Adjuvant', unit: 'litri' },
];

export const CROP_TYPES = [
  'Porumb', 'Grâu', 'Rapiță', 'Floarea-soarelui', 'Sfeclă de zahăr', 'Teren necultivat', 'Lucernă',
  'Fânețe', 'Legume proaspete', 'Cartofi', 'Soia', 'Orz', 'Ovăz',
];

export const UNITS = ['litri', 'kg', 'tone', 'bucăți'];
export const NO_CROP = 'Nespecificat';

// Old items keep one crop in targetCrop, newer ones a list in targetCrops.
export const targetCropsOf = (item) => {
  if (Array.isArray(item.targetCrops) && item.targetCrops.length > 0) return item.targetCrops;
  if (item.targetCrop && item.targetCrop !== NO_CROP) return [item.targetCrop];
  return [];
};

export const companyCropBreakdown = (companies, companyConfigs, invoices) => {
  const companyBreakdown = {};
  
  companies.forEach(company => {
    const config = { crops: {}, ...companyConfigs[company] };
    
    // Initialize company breakdown
    companyBreakdown[company] = {
      totalHectares: 0,
      totalInvestment: 0,
      crops: {},
      products: {
        general: {}, // Products for all crops (like Motorină)
        byCrop: {} // Products specific to each crop
      }
    };
    
    // Add crop data
    Object.entries(config.crops).forEach(([cropName, cropData]) => {
      companyBreakdown[company].crops[cropName] = {
        hectares: cropData.hectares || 0
      };
      companyBreakdown[company].totalHectares += cropData.hectares || 0;
      
      // Initialize product tracking for this crop
      companyBreakdown[company].products.byCrop[cropName] = {};
      PRODUCT_CATEGORIES.forEach(category => {
        companyBreakdown[company].products.byCrop[cropName][category.name] = {
          quantity: 0,
          value: 0,
          avgRecommendedConsumptionPerHa: 0,
          productNames: [],
          isFromMultipleCrops: false
        };
      });
    });
    
    // Initialize general products
    PRODUCT_CATEGORIES.forEach(category => {
      companyBreakdown[company].products.general[category.name] = {
        quantity: 0,
        value: 0,
        productNames: []
      };
    });
  });
  
  // Process invoices to populate product data
  invoices.forEach(invoice => {
    if (!invoice.items) return;
    
    
    invoice.items.forEach(item => {
      const company = invoice.company;
      const productType = item.productType;
      const targetCrop = item.targetCrop;
      const targetCrops = targetCropsOf(item); // Use helper function for backward compatibility
      const quantity = parseFloat(item.quantity) || 0;
      const value = quantity * (parseFloat(item.unitPrice) || 0);
      const consumptionPerHa = parseFloat(item.consumptionPerHa) || 0;
      
      
      if (!companyBreakdown[company]) return;
      
      // Add to total investment
      companyBreakdown[company].totalInvestment += value;
      
      if (productType === 'Motorină') {
        // General product for all crops (only Motorină now)
        if (companyBreakdown[company].products.general[productType]) {
          companyBreakdown[company].products.general[productType].quantity += quantity;
          companyBreakdown[company].products.general[productType].value += value;
          
          // Track product names
          if (item.productName && !companyBreakdown[company].products.general[productType].productNames.includes(item.productName)) {
            companyBreakdown[company].products.general[productType].productNames.push(item.productName);
          }
        }
      } else if (targetCrops.length > 0) {
        // Multi-crop product: Store in a shared pool for the company
        // This will be handled specially in transfer calculations
        if (!companyBreakdown[company].products.multiCropShared) {
          companyBreakdown[company].products.multiCropShared = {};
        }
        
        if (!companyBreakdown[company].products.multiCropShared[productType]) {
          companyBreakdown[company].products.multiCropShared[productType] = {
            quantity: 0,
            value: 0,
            targetCrops: new Set(),
            productNames: [],
            consumptionPerHa: consumptionPerHa || 0
          };
        }
        
        companyBreakdown[company].products.multiCropShared[productType].quantity += quantity;
        companyBreakdown[company].products.multiCropShared[productType].value += value;
        targetCrops.forEach(crop => companyBreakdown[company].products.multiCropShared[productType].targetCrops.add(crop));
        
        if (item.productName && !companyBreakdown[company].products.multiCropShared[productType].productNames.includes(item.productName)) {
          companyBreakdown[company].products.multiCropShared[productType].productNames.push(item.productName);
        }
        
        // Also add to individual crops for display purposes, but mark them specially
        targetCrops.forEach(cropName => {
          if (companyBreakdown[company].products.byCrop[cropName] && 
              companyBreakdown[company].products.byCrop[cropName][productType]) {
            
            // Store reference to shared pool instead of splitting quantity
            companyBreakdown[company].products.byCrop[cropName][productType].sharedPoolReference = true;
            companyBreakdown[company].products.byCrop[cropName][productType].isFromMultipleCrops = true;
            
            if (item.productName && !companyBreakdown[company].products.byCrop[cropName][productType].productNames.includes(item.productName)) {
              companyBreakdown[company].products.byCrop[cropName][productType].productNames.push(item.productName);
            }
            
            if (consumptionPerHa > 0) {
              const currentData = companyBreakdown[company].products.byCrop[cropName][productType];
              if (currentData.avgRecommendedConsumptionPerHa === 0) {
                currentData.avgRecommendedConsumptionPerHa = consumptionPerHa;
              } else {
                currentData.avgRecommendedConsumptionPerHa = (currentData.avgRecommendedConsumptionPerHa + consumptionPerHa) / 2;
              }
            }
          }
        });
      } else if (targetCrop && companyBreakdown[company].products.byCrop[targetCrop]) {
        // Fallback: single crop (backward compatibility)
        if (companyBreakdown[company].products.byCrop[targetCrop][productType]) {
          companyBreakdown[company].products.byCrop[targetCrop][productType].quantity += quantity;
          companyBreakdown[company].products.byCrop[targetCrop][productType].value += value;
          
          // Track product names
          if (item.productName && !companyBreakdown[company].products.byCrop[targetCrop][productType].productNames.includes(item.productName)) {
            companyBreakdown[company].products.byCrop[targetCrop][productType].productNames.push(item.productName);
          }
          
          // Store consumption per hectare data for transfer calculations
          if (consumptionPerHa > 0) {
            const currentData = companyBreakdown[company].products.byCrop[targetCrop][productType];
            // Average the consumption data (simple approach)
            if (currentData.avgRecommendedConsumptionPerHa === 0) {
              currentData.avgRecommendedConsumptionPerHa = consumptionPerHa;
            } else {
              // Simple average - could be improved with weighted average
              currentData.avgRecommendedConsumptionPerHa = (currentData.avgRecommendedConsumptionPerHa + consumptionPerHa) / 2;
            }
          }
        }
      }
    });
  });
  
  
  return companyBreakdown;
};

export const analyzeProducts = (companies, companyBreakdown) => {
  
  
  // Step 1: Calculate company-level needs and supplies for each product type
  const productTypeAnalysis = {};
  
  PRODUCT_CATEGORIES.forEach(category => {
    if (category.name === 'Motorină') return; // Skip general products
    
    const productType = category.name;
    productTypeAnalysis[productType] = {
      category: category,
      companies: {}
    };
    
    
    companies.forEach(company => {
      const breakdown = companyBreakdown[company];
      if (!breakdown) return;
      
      // Calculate total needs and supplies for this company across ALL crops for this product
      let totalCompanyNeed = 0;
      let totalCompanySupply = 0;
      let totalCompanyValue = 0;
      let totalHectares = 0;
      let avgConsumptionPerHa = 0;
      let consumptionDataPoints = 0;
      let cropDetails = [];
      
      // First, check if there's a shared multi-crop pool for this product type
      const sharedPool = breakdown.products?.multiCropShared?.[productType];
      let sharedPoolQuantity = 0;
      let sharedPoolValue = 0;
      let sharedPoolCrops = [];
      
      if (sharedPool) {
        sharedPoolQuantity = sharedPool.quantity || 0;
        sharedPoolValue = sharedPool.value || 0;
        sharedPoolCrops = Array.from(sharedPool.targetCrops || []);
      }
      
      // Calculate average consumption across all companies for this product type (for estimation)
      let globalAvgConsumption = 0;
      let globalConsumptionSamples = 0;
      
      // First pass: collect consumption data from all companies
      companies.forEach(otherCompany => {
        const otherBreakdown = companyBreakdown[otherCompany];
        if (!otherBreakdown) return;
        
        Object.entries(otherBreakdown.crops).forEach(([cropName, cropData]) => {
          const productData = otherBreakdown.products?.byCrop?.[cropName]?.[productType];
          if (productData && productData.avgRecommendedConsumptionPerHa > 0) {
            globalAvgConsumption += productData.avgRecommendedConsumptionPerHa;
            globalConsumptionSamples++;
          }
        });
        
        const otherSharedPool = otherBreakdown.products?.multiCropShared?.[productType];
        if (otherSharedPool && otherSharedPool.consumptionPerHa > 0) {
          globalAvgConsumption += otherSharedPool.consumptionPerHa;
          globalConsumptionSamples++;
        }
      });
      
      const estimatedConsumptionPerHa = globalConsumptionSamples > 0 ? globalAvgConsumption / globalConsumptionSamples : 0;
      
      
      // Analyze each crop for this product type
      Object.entries(breakdown.crops).forEach(([cropName, cropData]) => {
        const hectares = cropData.hectares || 0;
        const productData = breakdown.products?.byCrop?.[cropName]?.[productType];
        
        if (hectares > 0) {
          let currentSupply = 0;
          let currentValue = 0;
          let consumptionPerHa = 0;
          
          // Check if this crop has individual supply or uses shared pool
          if (productData && !productData.sharedPoolReference) {
            // Individual supply for this crop
            currentSupply = productData.quantity || 0;
            currentValue = productData.value || 0;
            consumptionPerHa = productData.avgRecommendedConsumptionPerHa || 0;
          } else if (sharedPool && sharedPoolCrops.includes(cropName)) {
            // This crop can use from the shared pool
            // For calculation purposes, the shared pool is available to satisfy all target crops
            currentSupply = sharedPoolQuantity; // Full shared pool available for this crop's needs
            currentValue = sharedPoolValue;
            consumptionPerHa = productData?.avgRecommendedConsumptionPerHa || sharedPool.consumptionPerHa || 0;
          }
          
          // If no consumption data, estimate from current supply
          if (consumptionPerHa === 0 && currentSupply > 0) {
            consumptionPerHa = currentSupply / hectares;
          }
          
          // UNIVERSAL CROP-MATCHING LOGIC: Only calculate needs for crops that have potential supply sources
          if (consumptionPerHa === 0 && estimatedConsumptionPerHa > 0) {
            // Check if ANY company has supply (individual or shared) for this crop
            const cropHasPotentialSupply = companies.some(otherCompany => {
              const otherBreakdown = companyBreakdown[otherCompany];
              if (!otherBreakdown) return false;
              
              // Check individual supply for this crop
              const individualSupply = otherBreakdown.products?.byCrop?.[cropName]?.[productType]?.quantity || 0;
              
              // Check shared pool that includes this crop
              const sharedPoolForCrop = otherBreakdown.products?.multiCropShared?.[productType];
              const sharedSupply = sharedPoolForCrop && Array.from(sharedPoolForCrop.targetCrops || []).includes(cropName) ? sharedPoolForCrop.quantity : 0;
              
              return individualSupply > 0 || sharedSupply > 0;
            });
            
            if (cropHasPotentialSupply) {
              // This crop has potential supply somewhere - calculate needs for transfer matching
              consumptionPerHa = estimatedConsumptionPerHa;
            } else {
              // No company has supply for this crop - skip to avoid artificial needs
            }
          }
          
          // Calculate needs even if no supply (companies with crops but no invoices)
          if (consumptionPerHa > 0) {
            avgConsumptionPerHa += consumptionPerHa;
            consumptionDataPoints++;
            
            const cropNeed = hectares * consumptionPerHa;
            totalCompanyNeed += cropNeed;
            totalHectares += hectares;
            
            cropDetails.push({
              cropName,
              hectares,
              need: cropNeed,
              supply: currentSupply,
              consumptionPerHa,
              isFromSharedPool: sharedPool && sharedPoolCrops.includes(cropName),
              isEstimated: currentSupply === 0 && consumptionPerHa === estimatedConsumptionPerHa
            });
            
          }
        }
      });
      
      // Calculate total supply: individual supplies + shared pool (but don't double count)
      let calculatedTotalSupply = 0;
      let calculatedTotalValue = 0;
      const processedSharedPools = new Set();
      
      Object.entries(breakdown.crops).forEach(([cropName, cropData]) => {
        const productData = breakdown.products?.byCrop?.[cropName]?.[productType];
        
        if (productData && !productData.sharedPoolReference) {
          // Individual supply
          calculatedTotalSupply += productData.quantity || 0;
          calculatedTotalValue += productData.value || 0;
        }
      });
      
      // Add shared pool once (not per crop)
      if (sharedPool && !processedSharedPools.has(productType)) {
        calculatedTotalSupply += sharedPoolQuantity;
        calculatedTotalValue += sharedPoolValue;
        processedSharedPools.add(productType);
      }
      
      totalCompanySupply = calculatedTotalSupply;
      totalCompanyValue = calculatedTotalValue;
      
      if (consumptionDataPoints > 0) {
        avgConsumptionPerHa = avgConsumptionPerHa / consumptionDataPoints;
        
        const companySurplus = Math.max(0, totalCompanySupply - totalCompanyNeed);
        const companyDeficit = Math.max(0, totalCompanyNeed - totalCompanySupply);
        
        productTypeAnalysis[productType].companies[company] = {
          totalNeed: totalCompanyNeed,
          totalSupply: totalCompanySupply,
          totalValue: totalCompanyValue,
          totalHectares: totalHectares,
          avgConsumptionPerHa: avgConsumptionPerHa,
          surplus: companySurplus,
          deficit: companyDeficit,
          cropDetails: cropDetails
        };
        
      }
    });
  });
  return productTypeAnalysis;
};

export const transferSuggestions = (productTypeAnalysis) => {
  const suggestions = [];
  // Step 2: Generate transfer suggestions based on company-level analysis
  Object.entries(productTypeAnalysis).forEach(([productType, analysis]) => {
    const category = analysis.category;
    const companyData = Object.entries(analysis.companies);
    
    if (companyData.length === 0) return;
    
    
    const companiesWithSurplus = companyData
      .filter(([_, data]) => data.surplus > 0.1)
      .sort(([_, a], [__, b]) => b.surplus - a.surplus);
      
    const companiesWithDeficit = companyData
      .filter(([_, data]) => data.deficit > 0.1)
      .sort(([_, a], [__, b]) => b.deficit - a.deficit);
    
    
    if (companiesWithSurplus.length > 0 && companiesWithDeficit.length > 0) {
      const transfers = [];
      
      // Create working copies for transfer calculation
      const workingSurplus = companiesWithSurplus.map(([company, data]) => ({
        company,
        ...data,
        availableForTransfer: data.surplus
      }));
      
      const workingDeficit = companiesWithDeficit.map(([company, data]) => ({
        company,
        ...data,
        needsFromTransfer: data.deficit
      }));
      
      // Generate optimal transfers
      workingSurplus.forEach(donor => {
        workingDeficit.forEach(recipient => {
          if (donor.availableForTransfer > 0.1 && recipient.needsFromTransfer > 0.1) {
            const transferQuantity = Math.min(donor.availableForTransfer, recipient.needsFromTransfer);
            const pricePerUnit = donor.totalValue > 0 ? donor.totalValue / donor.totalSupply : 0;
            const transferValue = transferQuantity * pricePerUnit;
            
            transfers.push({
              from: donor.company,
              to: recipient.company,
              fromHectares: donor.totalHectares,
              toHectares: recipient.totalHectares,
              fromCurrentSupply: donor.totalSupply,
              toCurrentSupply: recipient.totalSupply,
              fromNeed: donor.totalNeed,
              toNeed: recipient.totalNeed,
              transferQuantity: transferQuantity,
              transferValue: transferValue,
              pricePerUnit: pricePerUnit,
              fromCropDetails: donor.cropDetails,
              toCropDetails: recipient.cropDetails,
              efficiency: `${donor.company} are surplus de ${donor.surplus.toFixed(2)} ${category.unit} (după satisfacerea tuturor culturilor), ${recipient.company} are deficit de ${recipient.deficit.toFixed(2)} ${category.unit}`,
              impact: `După transfer: ${donor.company} va avea ${(donor.totalSupply - transferQuantity).toFixed(2)}/${donor.totalNeed.toFixed(2)} ${category.unit}, ${recipient.company} va avea ${(recipient.totalSupply + transferQuantity).toFixed(2)}/${recipient.totalNeed.toFixed(2)} ${category.unit}`,
              optimizationScore: (transferQuantity / Math.max(donor.surplus, recipient.deficit)) * 100
            });
            
            // Update for next iteration
            donor.availableForTransfer -= transferQuantity;
            recipient.needsFromTransfer -= transferQuantity;
          }
        });
      });
      
      if (transfers.length > 0) {
        // Sort transfers by optimization score
        transfers.sort((a, b) => b.optimizationScore - a.optimizationScore);
        
        // Calculate overall average consumption
        const avgConsumption = companyData.reduce((sum, [_, data]) => sum + data.avgConsumptionPerHa, 0) / companyData.length;
        const totalNeed = companyData.reduce((sum, [_, data]) => sum + data.totalNeed, 0);
        const totalSupply = companyData.reduce((sum, [_, data]) => sum + data.totalSupply, 0);
        
        suggestions.push({
          cropName: `Multi-cultură`, // Indicate this is multi-crop analysis
          productType: productType,
          unit: category.unit,
          averageConsumptionPerHa: avgConsumption,
          totalNeed: totalNeed,
          totalSupply: totalSupply,
          transfers: transfers,
          isMultiCropAnalysis: true
        });
      }
    }
  });
  
  return suggestions;
};

const cropsOf = (companyConfigs, company) => companyConfigs[company]?.crops || {};
const hectaresOf = (crops) => Object.values(crops).reduce((sum, crop) => sum + (crop.hectares || 0), 0);

// One line per product a company has or needs, for the Stocuri screen.
export const stockLines = (company, breakdown, analysis) => {
  const data = breakdown[company];
  if (!data) return [];
  const lines = [];

  Object.entries(data.products.general).forEach(([type, product]) => {
    if (product.quantity <= 0) return;
    const category = PRODUCT_CATEGORIES.find((item) => item.name === type);
    lines.push({
      company, type, general: true, unit: category?.unit || '', quantity: product.quantity, value: product.value,
      need: null, names: product.productNames, crops: [], cropDetails: [], consumption: 0,
    });
  });

  const pools = data.products.multiCropShared || {};
  const types = [...new Set([...PRODUCT_CATEGORIES.map((item) => item.name).filter((name) => name !== 'Motorină'), ...Object.keys(pools)])];
  types.forEach((type) => {
    const known = analysis[type]?.companies?.[company];
    const pool = pools[type];
    const single = Object.values(data.products.byCrop).reduce((sum, crop) => sum + (crop[type]?.quantity || 0), 0);
    const singleValue = Object.values(data.products.byCrop).reduce((sum, crop) => sum + (crop[type]?.value || 0), 0);
    const quantity = known ? known.totalSupply : (pool?.quantity || 0) + single;
    const value = known ? known.totalValue : (pool?.value || 0) + singleValue;
    if (quantity <= 0 && !(known?.totalNeed > 0)) return;

    const names = new Set(pool?.productNames || []);
    Object.values(data.products.byCrop).forEach((crop) => (crop[type]?.productNames || []).forEach((name) => names.add(name)));
    lines.push({
      company, type, general: false,
      unit: PRODUCT_CATEGORIES.find((item) => item.name === type)?.unit || '',
      quantity, value, need: known ? known.totalNeed : null,
      names: [...names], crops: pool ? [...pool.targetCrops] : [],
      cropDetails: known?.cropDetails || [], consumption: known?.avgConsumptionPerHa || 0,
    });
  });
  return lines;
};

export const lineStatus = (line) => {
  if (line.need === null || line.need <= 0) return 'unknown';
  if (line.quantity >= line.need) return 'ok';
  return line.quantity >= line.need * 0.8 ? 'near' : 'low';
};

export const companyStats = (company, companyConfigs, allocations, invoices) => {
  const crops = cropsOf(companyConfigs, company);
  const totalHectares = hectaresOf(crops);
  const invoiceIds = new Set(invoices.map((invoice) => String(invoice.id)));
  let totalAllocatedCost = 0;
  Object.entries(allocations).forEach(([key, allocation]) => {
    // An allocation left behind by a deleted invoice no longer counts as a cost.
    if (!invoiceIds.has(key.slice(0, key.lastIndexOf('_')))) return;
    Object.values(allocation?.companyAllocations?.[company]?.crops || {}).forEach((crop) => {
      totalAllocatedCost += crop.allocatedValue || 0;
    });
  });
  return {
    totalHectares, cropCount: Object.keys(crops).length, totalAllocatedCost,
    costPerHectare: totalHectares > 0 ? totalAllocatedCost / totalHectares : 0,
  };
};

// Splits one invoice line over all companies and their crops, by hectares.
export const optimalAllocation = (item, companies, companyConfigs) => {
  const totalHectares = companies.reduce((sum, company) => sum + hectaresOf(cropsOf(companyConfigs, company)), 0);
  if (totalHectares === 0) return {};

  const allocation = { companyAllocations: {} };
  companies.forEach((company) => {
    const crops = cropsOf(companyConfigs, company);
    const companyHectares = hectaresOf(crops);
    const companyPercentage = companyHectares / totalHectares;
    allocation.companyAllocations[company] = {
      percentage: companyPercentage * 100,
      allocatedQuantity: item.quantity * companyPercentage,
      allocatedValue: (item.quantity * item.unitPrice) * companyPercentage,
      crops: {},
    };
    Object.entries(crops).forEach(([cropName, cropData]) => {
      const cropPercentage = cropData.hectares / companyHectares;
      allocation.companyAllocations[company].crops[cropName] = {
        hectares: cropData.hectares,
        percentage: cropPercentage * 100,
        allocatedQuantity: (item.quantity * companyPercentage) * cropPercentage,
        allocatedValue: ((item.quantity * item.unitPrice) * companyPercentage) * cropPercentage,
      };
    });
  });
  return allocation;
};

export const invoiceAllocations = (invoice, companies, companyConfigs) => {
  const result = {};
  (invoice.items || []).forEach((item, index) => {
    result[`${invoice.id}_${index}`] = optimalAllocation(item, companies, companyConfigs);
  });
  return result;
};

export const setupIssues = (companies, companyConfigs) => {
  const issues = [];
  if (companies.length === 0) issues.push('Nu sunt firme. Le adaugi din Setări.');
  companies.forEach((company) => {
    if (Object.keys(cropsOf(companyConfigs, company)).length === 0) issues.push(`${company} nu are culturi.`);
  });
  return issues;
};

const detailedStats = (companies, companyConfigs, invoices, allocations) => {
  const stats = {
    totalCompanies: companies.length, totalHectares: 0, totalInvoices: invoices.length,
    totalInvoiceValue: 0, totalAllocatedValue: 0, companiesStats: {}, productStats: {},
  };
  companies.forEach((company) => {
    const companyData = companyStats(company, companyConfigs, allocations, invoices);
    stats.companiesStats[company] = companyData;
    stats.totalHectares += companyData.totalHectares;
  });
  invoices.forEach((invoice) => {
    stats.totalInvoiceValue += invoice.totalValue || 0;
    (invoice.items || []).forEach((item, index) => {
      const allocation = allocations[`${invoice.id}_${index}`];
      Object.values(allocation?.companyAllocations || {}).forEach((share) => {
        stats.totalAllocatedValue += share.allocatedValue || 0;
      });
      if (!stats.productStats[item.productType]) stats.productStats[item.productType] = { totalQuantity: 0, totalValue: 0, invoiceCount: 0 };
      stats.productStats[item.productType].totalQuantity += item.quantity || 0;
      stats.productStats[item.productType].totalValue += (item.quantity * item.unitPrice) || 0;
      stats.productStats[item.productType].invoiceCount += 1;
    });
  });
  stats.allocationPercentage = stats.totalInvoiceValue > 0 ? (stats.totalAllocatedValue / stats.totalInvoiceValue) * 100 : 0;
  stats.avgCostPerHectare = stats.totalHectares > 0 ? stats.totalAllocatedValue / stats.totalHectares : 0;
  return stats;
};

// The same report the old page exported.
export const complianceReport = ({ farm, companies, companyConfigs, invoices, allocations, now = new Date().toISOString() }) => {
  const summary = detailedStats(companies, companyConfigs, invoices, allocations);
  return {
    generatedAt: now,
    farmInfo: farm,
    summary,
    companies: companies.map((name) => ({ name, config: companyConfigs[name] || { crops: {} }, stats: summary.companiesStats[name] || {} })),
    invoices,
    allocations,
  };
};

// ---------- Invoice form ----------

export const emptyRow = () => ({
  productType: '', productName: '', quantity: '', unit: 'litri', unitPrice: '',
  targetCrop: '', targetCrops: [], isMultipleCrops: false, consumptionPerHa: '',
});

export const emptyForm = (company = '', today = new Date().toISOString().split('T')[0]) => ({
  number: '', company, date: today, status: 'Plătită', supplier: '', rows: [emptyRow()],
});

export const formFromInvoice = (invoice) => ({
  number: invoice.number || '',
  company: invoice.company || '',
  date: invoice.date || '',
  status: invoice.status || 'Plătită',
  supplier: invoice.supplier || '',
  rows: (invoice.items?.length ? invoice.items : [emptyRow()]).map((item) => {
    const crops = targetCropsOf(item);
    const several = crops.length > 1;
    return {
      productType: item.productType || '',
      productName: item.productName || '',
      quantity: String(item.quantity ?? ''),
      unit: item.unit || 'litri',
      unitPrice: String(item.unitPrice ?? ''),
      targetCrop: several ? '' : crops[0] || '',
      targetCrops: several ? crops : [],
      isMultipleCrops: several,
      consumptionPerHa: item.consumptionPerHa ? String(item.consumptionPerHa) : '',
    };
  }),
});

const isFuel = (row) => row.productType === 'Motorină';
const numberOf = (text) => parseFloat(String(text).replace(',', '.'));

export const rowTotal = (row) => (numberOf(row.quantity) || 0) * (numberOf(row.unitPrice) || 0);
export const formTotal = (form) => form.rows.reduce((sum, row) => sum + rowTotal(row), 0);

export const formErrors = (form) => {
  const errors = {};
  if (!form.number.trim()) errors.number = true;
  if (!form.company) errors.company = true;
  if (!form.date) errors.date = true;
  if (!form.supplier.trim()) errors.supplier = true;
  form.rows.forEach((row, index) => {
    if (!row.productType) errors[`type${index}`] = true;
    if (!(numberOf(row.quantity) >= 0)) errors[`quantity${index}`] = true;
    if (!(numberOf(row.unitPrice) >= 0)) errors[`price${index}`] = true;
    const crop = row.isMultipleCrops ? row.targetCrops.length > 0 : Boolean(row.targetCrop);
    if (!isFuel(row) && !crop) errors[`crop${index}`] = true;
  });
  return errors;
};

// Same saved shape as always.
export const buildInvoice = (form, editing, now = new Date().toISOString()) => {
  const items = form.rows.map((row) => {
    const quantity = numberOf(row.quantity);
    const unitPrice = numberOf(row.unitPrice);
    const crops = !isFuel(row) && row.isMultipleCrops ? row.targetCrops : [];
    let targetCrop = NO_CROP;
    if (crops.length > 1) targetCrop = 'Multiple';
    else if (crops.length === 1) [targetCrop] = crops;
    else if (!isFuel(row) && row.targetCrop) targetCrop = row.targetCrop;
    const consumption = numberOf(row.consumptionPerHa);
    return {
      productType: row.productType,
      productName: isFuel(row) ? '' : row.productName.trim(),
      quantity,
      unitPrice,
      unit: row.unit || 'litri',
      targetCrop,
      targetCrops: crops,
      isMultipleCrops: crops.length > 1,
      consumptionPerHa: !isFuel(row) && consumption ? consumption : null,
      totalValue: quantity * unitPrice,
    };
  });
  return {
    ...(editing || {}),
    id: editing?.id || Date.now().toString(),
    number: form.number.trim(),
    company: form.company,
    date: form.date,
    status: form.status,
    supplier: form.supplier.trim(),
    items,
    totalValue: items.reduce((sum, item) => sum + item.totalValue, 0),
    allocated: true,
    allocationPercentage: 100,
    createdAt: editing?.createdAt || now,
    updatedAt: now,
  };
};

const plain = (text) => String(text ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

export const matchesInvoice = (invoice, search) => {
  const term = plain(search).trim();
  if (!term) return true;
  const fields = [invoice.number, invoice.company, invoice.supplier, invoice.status,
    ...(invoice.items || []).flatMap((item) => [item.productType, item.productName])];
  return fields.some((field) => plain(field).includes(term));
};

// What the worker inventory receives when a new invoice is added. Unchanged from the old page.
export const addInvoiceToInventory = (invoice, current) => {
  const materials = current?.materials || [];
  const equipment = current?.equipment || [];
  invoice.items.forEach((item) => {
    const inventoryItem = {
      id: `inv_${invoice.id}_${item.productName}_${Date.now()}`,
      name: item.productName || `${item.productType} - ${invoice.supplier}`,
      type: item.productType || 'Material',
      quantity: parseFloat(item.quantity) || 0,
      unit: item.unit || 'bucăți',
      unitPrice: parseFloat(item.unitPrice) || 0,
      supplier: invoice.supplier || 'Unknown',
      date: invoice.date || new Date().toISOString().split('T')[0],
      location: 'Depozit Principal',
      notes: `Din factura ${invoice.number} - ${invoice.company}`,
      status: 'available',
      createdAt: new Date().toISOString(),
      totalValue: (parseFloat(item.quantity) || 0) * (parseFloat(item.unitPrice) || 0),
      sourceInvoiceId: invoice.id,
      targetCrop: item.targetCrop,
      targetCrops: item.targetCrops || [],
    };
    materials.push(inventoryItem);
  });
  return { materials, equipment, invoices: current?.invoices || [] };
};
